import { useEffect, useMemo, useRef } from 'react'
import { useIntl } from 'react-intl'
import { useControl } from 'react-map-gl/mapbox'
import type { ControlPosition, IControl, MapInstance } from 'react-map-gl/mapbox'
import {
  getMapPitchCamera,
  getPitchModeFromPitch,
  MAP_FLAT_CAMERA,
  type MapPitchMode,
} from './map3DConfig'

const PITCH_TOGGLE_DURATION_MS = 500

type ControlLabels = {
  tiltTo3D: string
  returnTo2D: string
}

export function is3DMapPitch(pitch: number): boolean {
  return getPitchModeFromPitch(pitch) === '3d'
}

export function getPitchToggleLabel(pitch: number): '2D' | '3D' {
  return is3DMapPitch(pitch) ? '2D' : '3D'
}

function prefersReducedMotion(): boolean {
  return Boolean(
    typeof window !== 'undefined' &&
    window.matchMedia?.('(prefers-reduced-motion: reduce)').matches,
  )
}

class MapPitchToggleControlImpl implements IControl {
  private container: HTMLDivElement
  private button: HTMLButtonElement
  private map?: MapInstance
  private onPitchModeChange?: (mode: MapPitchMode) => void
  private labels: ControlLabels
  private reportedMode?: MapPitchMode

  constructor(onPitchModeChange: ((mode: MapPitchMode) => void) | undefined, labels: ControlLabels) {
    this.onPitchModeChange = onPitchModeChange
    this.labels = labels
    this.container = document.createElement('div')
    this.container.className = 'mapboxgl-ctrl mapboxgl-ctrl-group'

    this.button = document.createElement('button')
    this.button.type = 'button'
    this.button.style.alignItems = 'center'
    this.button.style.color = '#3f4754'
    this.button.style.display = 'flex'
    this.button.style.fontSize = '11px'
    this.button.style.fontWeight = '700'
    this.button.style.justifyContent = 'center'
    this.button.style.letterSpacing = '0'
    this.button.style.lineHeight = '1'

    this.container.append(this.button)
  }

  onAdd(map: MapInstance): HTMLElement {
    this.map = map
    this.button.addEventListener('click', this.handleClick)
    map.on('moveend', this.handleCameraChange)
    map.on('pitchend', this.handleCameraChange)
    map.on('rotateend', this.handleCameraChange)
    this.updateButton()
    this.reportPitchMode(getPitchModeFromPitch(map.getPitch()))

    return this.container
  }

  onRemove(): void {
    this.button.removeEventListener('click', this.handleClick)
    this.map?.off('moveend', this.handleCameraChange)
    this.map?.off('pitchend', this.handleCameraChange)
    this.map?.off('rotateend', this.handleCameraChange)
    this.container.remove()
    this.map = undefined
  }

  private handleCameraChange = () => {
    const map = this.map
    const pitch = map?.getPitch() ?? MAP_FLAT_CAMERA.pitch
    this.updateButton(pitch)
    this.reportPitchMode(getPitchModeFromPitch(pitch))
  }

  private handleClick = (event: MouseEvent) => {
    event.preventDefault()
    event.stopPropagation()

    const map = this.map
    if (!map) {
      return
    }

    map.stop()
    const nextMode: MapPitchMode = is3DMapPitch(map.getPitch()) ? '2d' : '3d'
    const nextCamera = getMapPitchCamera(nextMode)

    if (prefersReducedMotion()) {
      map.jumpTo(nextCamera)
    } else {
      map.easeTo({
        ...nextCamera,
        duration: PITCH_TOGGLE_DURATION_MS,
        essential: true,
      })
    }

    this.updateButton(nextCamera.pitch)
    this.reportPitchMode(nextMode)
  }

  private updateButton(pitch = this.map?.getPitch() ?? MAP_FLAT_CAMERA.pitch) {
    const label = getPitchToggleLabel(pitch)
    const ariaLabel = label === '3D' ? this.labels.tiltTo3D : this.labels.returnTo2D

    this.button.textContent = label
    this.button.setAttribute('aria-label', ariaLabel)
    this.button.title = ariaLabel
  }

  private reportPitchMode(mode: MapPitchMode) {
    if (this.reportedMode === mode) return
    this.reportedMode = mode
    this.onPitchModeChange?.(mode)
  }

  setLabels(labels: ControlLabels) {
    this.labels = labels
    this.updateButton()
  }
}

type Props = {
  position?: ControlPosition
  onPitchModeChange?: (mode: MapPitchMode) => void
}

function MapPitchToggleControl({ position = 'bottom-right', onPitchModeChange }: Props) {
  const intl = useIntl()
  const onPitchModeChangeRef = useRef(onPitchModeChange)
  const labels = useMemo(() => ({
    tiltTo3D: intl.formatMessage({
      id: 'LwVdn6yb4N9pQemMyPrS',
      defaultMessage: 'Tilt map to 3D',
      description: 'Accessible label for the Trip Planner map control that tilts the map into 3D',
    }),
    returnTo2D: intl.formatMessage({
      id: 'ET3gMu6_MCFvRynLqOmj',
      defaultMessage: 'Return map to 2D',
      description: 'Accessible label for the Trip Planner map control that returns the map to a flat 2D view',
    }),
  }), [intl])

  useEffect(() => {
    onPitchModeChangeRef.current = onPitchModeChange
  }, [onPitchModeChange])

  const control = useControl(
    () => new MapPitchToggleControlImpl((mode) => onPitchModeChangeRef.current?.(mode), labels),
    { position },
  )

  useEffect(() => {
    control.setLabels(labels)
  }, [control, labels])

  return null
}

export default MapPitchToggleControl
